import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush,
  Legend,
} from 'recharts';

// Dark-themed Recharts LineChart used on /monitoring/outlook. Tremor's
// LineChart is great but doesn't expose a brush, so we go to Recharts directly
// for the timeseries charts that need range-select. The Tooltip/grid colors
// here mirror what Tremor renders in dark mode so the charts visually slot in
// next to the Tremor cards on the page.

export interface RangeSelectChartSeries {
  /** dataKey on each row */
  key: string;
  /** Human-readable label shown in legend / tooltip */
  label: string;
  /** Tailwind-ish color name → resolved to hex below */
  color: 'cyan' | 'violet' | 'amber' | 'emerald' | 'rose' | 'sky' | 'gray';
  /** Start with the line hidden — user clicks the legend item to reveal. */
  defaultHidden?: boolean;
}

export interface RangeSelectChartProps<Row extends Record<string, unknown>> {
  data: Row[];
  /** Field name on each row used for x-axis labels (already-formatted string). */
  xKey: keyof Row & string;
  series: RangeSelectChartSeries[];
  /** Format y-axis values (ms / count / etc). */
  valueFormatter?: (v: number | null | undefined) => string;
  /** Sync hover/tooltip with other charts sharing the same id. */
  syncId?: string;
  /** When set, the chart renders a Brush at the bottom. */
  showBrush?: boolean;
  /** Controlled brush window (indexes into `data`). Pass to keep multiple charts in lock-step. */
  brushStart?: number;
  brushEnd?: number;
  onBrushChange?: (start: number, end: number) => void;
  /** Tailwind height class. Defaults to "h-56". */
  heightClass?: string;
  /** Hide the legend (useful for single-series charts). */
  hideLegend?: boolean;
}

const COLOR_MAP: Record<RangeSelectChartSeries['color'], string> = {
  cyan: '#22d3ee',
  violet: '#a78bfa',
  amber: '#f59e0b',
  emerald: '#34d399',
  rose: '#fb7185',
  sky: '#38bdf8',
  gray: '#9ca3af',
};

const AXIS_TICK_STYLE = { fill: '#6b7280', fontSize: 11 };

function defaultFormatter(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString();
}

export function RangeSelectChart<Row extends Record<string, unknown>>({
  data,
  xKey,
  series,
  valueFormatter = defaultFormatter,
  syncId,
  showBrush = false,
  brushStart,
  brushEnd,
  onBrushChange,
  heightClass = 'h-56',
  hideLegend = false,
}: RangeSelectChartProps<Row>) {
  // Click a legend item → toggle its series visibility. Initialized from each
  // series's `defaultHidden` so a chart can ship with extra lines hidden until
  // the user opts in (e.g., "Fetch p95 (all attempts)" stays off so a hedge
  // outlier doesn't dominate the chart).
  const [hidden, setHidden] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(series.filter((s) => s.defaultHidden).map((s) => [s.key, true])),
  );
  // Recharts types DataKey as `string | number | ((obj) => any)`. Our series
  // always use string dataKeys so we narrow at runtime.
  const handleLegendClick = (entry: unknown) => {
    const k = (entry as { dataKey?: unknown })?.dataKey;
    if (typeof k !== 'string') return;
    setHidden((h) => ({ ...h, [k]: !h[k] }));
  };

  const lines = useMemo(
    () =>
      series.map((s) => (
        <Line
          key={s.key}
          type="monotone"
          dataKey={s.key}
          name={s.label}
          stroke={COLOR_MAP[s.color]}
          strokeWidth={1.75}
          dot={false}
          activeDot={{ r: 3 }}
          connectNulls
          isAnimationActive={false}
          hide={!!hidden[s.key]}
        />
      )),
    [series, hidden],
  );

  const handleBrushChange = (range: { startIndex?: number; endIndex?: number } | null) => {
    if (!range || !onBrushChange) return;
    const { startIndex, endIndex } = range;
    if (typeof startIndex === 'number' && typeof endIndex === 'number') {
      onBrushChange(startIndex, endIndex);
    }
  };

  return (
    <div className={`w-full ${heightClass}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }} syncId={syncId}>
          <CartesianGrid stroke="#1f2937" strokeDasharray="2 2" vertical={false} />
          <XAxis
            dataKey={xKey}
            tick={AXIS_TICK_STYLE}
            stroke="#374151"
            tickLine={false}
            minTickGap={32}
          />
          <YAxis
            tick={AXIS_TICK_STYLE}
            stroke="#374151"
            tickLine={false}
            width={56}
            tickFormatter={(v: number) => valueFormatter(v)}
            domain={['auto', 'auto']}
          />
          <Tooltip
            content={
              <FullSeriesTooltip
                series={series}
                hidden={hidden}
                valueFormatter={valueFormatter}
              />
            }
          />
          {!hideLegend && (
            <Legend
              wrapperStyle={{ fontSize: 12, color: '#9ca3af', paddingTop: 4, cursor: 'pointer' }}
              iconType="plainline"
              onClick={handleLegendClick}
              formatter={(value: string, entry: unknown) => {
                const k = (entry as { dataKey?: unknown })?.dataKey;
                const isHidden = typeof k === 'string' ? !!hidden[k] : false;
                return (
                  <span
                    style={{
                      color: isHidden ? '#4b5563' : '#9ca3af',
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                    title="Click to toggle"
                  >
                    {value}
                  </span>
                );
              }}
            />
          )}
          {lines}
          {showBrush && data.length > 1 && (
            <Brush
              dataKey={xKey}
              height={26}
              startIndex={brushStart}
              endIndex={brushEnd}
              onChange={handleBrushChange}
              travellerWidth={8}
              stroke="#4b5563"
              fill="#0b1220"
              tickFormatter={() => ''}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Custom tooltip that ALWAYS shows every series (rendering "—" for null /
// missing values) and skips hidden ones. Recharts' default tooltip only lists
// series with a non-null value at the active X, so a sparse bucket — e.g., one
// where a losing hedge fetch landed alone — would show only the fetch series
// and leave the user wondering where the others went.
function FullSeriesTooltip({
  series,
  hidden,
  valueFormatter,
  active,
  payload,
  label,
}: {
  series: RangeSelectChartSeries[];
  hidden: Record<string, boolean>;
  valueFormatter: (v: number | null | undefined) => string;
  active?: boolean;
  // Recharts injects these; types are loose because of the generic-row chart.
  payload?: Array<{ payload?: Record<string, unknown> }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload || {};
  return (
    <div
      style={{
        background: '#0b1220',
        border: '1px solid #1f2937',
        borderRadius: 6,
        fontSize: 12,
        color: '#e5e7eb',
        padding: '6px 10px',
        lineHeight: 1.6,
      }}
    >
      <div style={{ color: '#9ca3af', marginBottom: 4 }}>{label}</div>
      {series
        .filter((s) => !hidden[s.key])
        .map((s) => {
          const raw = row[s.key];
          const v = typeof raw === 'number' ? raw : null;
          return (
            <div key={s.key} style={{ color: '#e5e7eb' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  background: COLOR_MAP[s.color],
                  borderRadius: 2,
                  marginRight: 6,
                  verticalAlign: 'middle',
                }}
              />
              <span style={{ color: '#9ca3af' }}>{s.label}: </span>
              <span>{valueFormatter(v)}</span>
            </div>
          );
        })}
    </div>
  );
}
